import React, { useCallback, useEffect, useMemo } from 'react';
import { Autocomplete, AutocompleteProps, Box } from '@p81-common/ui';
import type { AutocompleteOptionProps } from '@p81-common/ui';
import { Heading } from 'components/common/Elements/Elements';
import classes from './FiltersBlock.module.scss';
import { useTranslation } from 'react-i18next';

interface AutocompleteFilterProps {
  autocompleteOptionsMultiple: AutocompleteOptionProps[];
  setAutocompleteOptionsMultiple: React.Dispatch<
    React.SetStateAction<AutocompleteOptionProps[]>
  >;
  handleFilter: (e: any) => void;
}

const FILTER_NAME = 'customerIds';

const AutocompleteFilter: React.FC<AutocompleteFilterProps> = ({
  autocompleteOptionsMultiple,
  setAutocompleteOptionsMultiple,
  handleFilter
}) => {
  const { t } = useTranslation(['activity']);

  useEffect(() => {
    const arrayOfValues = autocompleteOptionsMultiple.map(obj => obj.name);
    const e = {
      target: FILTER_NAME,
      value: arrayOfValues
    };
    handleFilter(e);
    // eslint-disable-next-line
  }, [autocompleteOptionsMultiple]);

  const handleAutocompleteCreate: AutocompleteProps['handleCreate'] =
    useCallback(
      (e, v) => {
        if (e.type === 'blur') return;
        const value = v[v.length - 1]
          ?.toString()
          .trim()
          .replace(/,/g, ' ')
          .replace(/\t+/g, ' ');
        const newValue = value.split(' ');
        const uniqueOptions = new Set(
          [
            ...autocompleteOptionsMultiple.map(option => option.name),
            ...newValue
          ].filter(str => str !== '')
        );
        const updatedOptions = Array.from(uniqueOptions).map(name => ({
          name,
          selected: true
        }));

        setAutocompleteOptionsMultiple(updatedOptions);
      },
      [autocompleteOptionsMultiple, setAutocompleteOptionsMultiple]
    );

  const handleAutocompleteDelete: AutocompleteProps['handleDelete'] = (
    e,
    v
  ) => {
    if ((e as React.KeyboardEvent).key === 'Enter') {
      return;
    }
    setAutocompleteOptionsMultiple(v);
  };

  const handleAutocompleteClear: AutocompleteProps['handleClear'] = () => {
    setAutocompleteOptionsMultiple([]);
  };

  const autocompleteValueMultiple = useMemo(
    () => autocompleteOptionsMultiple.filter(o => o.selected),
    [autocompleteOptionsMultiple]
  );

  return (
    <Box>
      <Heading className={classes.title}>
        {t('activity:agentRollout.LIST_OF_CUSTOMER_IDS')}
      </Heading>
      <Autocomplete
        className={classes.autocomplete}
        handleClear={handleAutocompleteClear}
        handleCreate={handleAutocompleteCreate}
        handleDelete={handleAutocompleteDelete}
        value={autocompleteValueMultiple}
        options={autocompleteOptionsMultiple}
        size={'medium'}
        multiple={true}
        defaultLimitTags={-1}
        alwaysExpand={true}
        showLimitTagsTooltip={true}
        freeSolo={true}
        placeholder={
          autocompleteOptionsMultiple.length
            ? ''
            : t('activity:agentRollout.AUTOCOMPLETE_PLACEHOLDER_CUSTOMER_IDS')
        }
      />
    </Box>
  );
};

export default AutocompleteFilter;
